import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { VpNotification, VpNotificationKind } from "@vp/shared/notification-service";
import { VpLiveTimerDirective, VpStatusPillComponent } from "@vp/shared/ui/primitives";

const KICKERS: Partial<Record<VpNotificationKind, string>> = {
  "call-light": "Alert",
  "call-light-urgent": "Urgent Alert",
  assignment: "Assignment",
  movement: "Movement",
  "case-updated": "Case update"
};

/** The short label naming what kind of event a notification records, if it has one. */
export function notificationKicker(n: VpNotification): string | null {
  return KICKERS[n.kind] ?? null;
}

/** An unresolved urgent notification: it can't be dismissed or tucked. */
export function isLockedNotification(n: VpNotification): boolean {
  return n.urgent && !n.resolved;
}

/**
 * The meta row of a sticky notification: kicker, urgent or cleared status, and
 * either a live "Active" timer or the time it arrived. Renders its parts as
 * direct flex items of the toast's meta row.
 */
@Component({
  selector: "vp-notification-meta",
  imports: [DatePipe, VpLiveTimerDirective, VpStatusPillComponent],
  templateUrl: "./vp-notification-meta.component.html",
  styleUrls: ["./vp-notification-meta.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VpNotificationMetaComponent {
  readonly notification = input.required<VpNotification>();

  protected readonly kicker = computed(() => notificationKicker(this.notification()));
  protected readonly locked = computed(() => isLockedNotification(this.notification()));

  protected readonly clearedLabel = computed(() => {
    const by = this.notification().resolvedBy;
    return by ? `Cleared · ${by}` : "Cleared";
  });

  /** A live light counts up from its activation; anything else shows when it arrived. */
  protected readonly activeSince = computed(() => {
    const n = this.notification();
    return n.kind === "call-light" && !n.resolved && Number.isFinite(n.activatedAt)
      ? (n.activatedAt as number)
      : null;
  });
}
